import {
  Button,
  DateInput,
  Modal,
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
  addToast,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { toDateOnlyPayload } from '../../../../../utils/dates';
import { createCharge, getChargeCurrencies } from '../api';
import { ChargeItem, Debt } from '../types';
import { useDebtLoader } from './DebtLoader';

const keyPrefix =
  'financialPortal.currentAccount.tabs.currentAccount.newCharge';

// Sentinel value used for the "Otro" option in the charge-item selector.
const OTHER_VALUE = -1;

interface NewChargeModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: number;
  onSuccess: () => void;
  chargeItems: ChargeItem[];
}

interface FormShape {
  studyPlan: SelectOptionType | null;
  period: SelectOptionType | null;
  chargeItem: SelectOptionType | null;
  concept: string;
  expirationDate: string;
  costCenter: SelectOptionType | null;
  currency: SelectOptionType | null;
  amount: string;
}

const EMPTY_FORM: FormShape = {
  studyPlan: null,
  period: null,
  chargeItem: null,
  concept: '',
  expirationDate: '',
  costCenter: null,
  currency: null,
  amount: '',
};

function uniqueBy<T, K>(items: T[], keyFn: (item: T) => K): T[] {
  const seen = new Set<K>();
  const out: T[] = [];
  for (const item of items) {
    const key = keyFn(item);
    if (!seen.has(key)) {
      seen.add(key);
      out.push(item);
    }
  }
  return out;
}

export default function NewChargeModal({
  isOpen,
  onClose,
  studentId,
  onSuccess,
  chargeItems,
}: NewChargeModalProps) {
  const { t } = useTranslation();
  const { data } = useDebtLoader();
  const debts = useMemo<Debt[]>(() => data?.debts ?? [], [data?.debts]);

  const [currencyOptions, setCurrencyOptions] = useState<SelectOptionType[]>(
    [],
  );

  useEffect(() => {
    let cancelled = false;
    getChargeCurrencies().then(({ data, error }) => {
      if (cancelled) {
        return;
      }
      if (error || !data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${keyPrefix}.currencyLoadError`),
        });
        return;
      }
      setCurrencyOptions(data.map((c) => ({ label: c.code, value: c.code })));
    });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormShape>({
    defaultValues: EMPTY_FORM,
  });

  const selectedStudyPlan = useWatch({ control, name: 'studyPlan' });
  const selectedPeriod = useWatch({ control, name: 'period' });
  const selectedItem = useWatch({ control, name: 'chargeItem' });
  const isOther = selectedItem?.value === OTHER_VALUE;
  const isItemSelected = selectedItem !== null && selectedItem !== undefined;
  const isPlanAndPeriodSelected =
    selectedStudyPlan !== null && selectedPeriod !== null;

  // Plan options: distinct study plans the student has debts on.
  const studyPlanOptions = useMemo<SelectOptionType[]>(() => {
    const plans = debts
      .filter(
        (d): d is Debt & { studyPlanId: number; studyPlanName: string } =>
          d.studyPlanId !== null && d.studyPlanName !== null,
      )
      .map((d) => ({ id: d.studyPlanId, name: d.studyPlanName }));
    return uniqueBy(plans, (p) => p.id).map((p) => ({
      label: p.name,
      value: p.id,
    }));
  }, [debts]);

  // Period options: distinct periods within the selected plan.
  const periodOptions = useMemo<SelectOptionType[]>(() => {
    if (selectedStudyPlan?.value == null) {
      return [];
    }
    const planId = selectedStudyPlan.value as number;
    const periods = debts
      .filter(
        (d): d is Debt & { periodId: number; periodName: string } =>
          d.studyPlanId === planId &&
          d.periodId !== null &&
          d.periodName !== null,
      )
      .map((d) => ({ id: d.periodId, name: d.periodName }));
    return uniqueBy(periods, (p) => p.id).map((p) => ({
      label: p.name,
      value: p.id,
    }));
  }, [debts, selectedStudyPlan]);

  // Resolve the full ChargeItem when a preconfigured item is selected.
  const resolvedItem = useMemo<ChargeItem | null>(() => {
    if (!selectedItem || isOther) {
      return null;
    }
    return chargeItems.find((ci) => ci.id === selectedItem.value) ?? null;
  }, [selectedItem, isOther, chargeItems]);

  // Reset form when opening/closing.
  useEffect(() => {
    if (isOpen) {
      reset(EMPTY_FORM);
    }
  }, [isOpen, reset]);

  // When the selected plan changes, clear the period (its options change).
  useEffect(() => {
    setValue('period', null);
  }, [selectedStudyPlan, setValue]);

  // Cost center options: distinct, non-empty cost centers seen across the
  // preconfigured chargeItems. Used both to display the readonly value when a
  // preconfigured item is picked and to let "Otro" pick from the same set
  // instead of typing free text (HU-15669 fix).
  const costCenterOptions = useMemo<SelectOptionType[]>(() => {
    const centers = chargeItems
      .map((ci) => ci.costCenter)
      .filter((c): c is string => !!c && c.trim() !== '');
    return Array.from(new Set(centers)).map((c) => ({ label: c, value: c }));
  }, [chargeItems]);

  // When a preconfigured item is selected, populate readonly fields.
  useEffect(() => {
    if (resolvedItem) {
      setValue(
        'costCenter',
        resolvedItem.costCenter
          ? costCenterOptions.find(
              (o) => o.value === resolvedItem.costCenter,
            ) ?? null
          : null,
      );
      setValue(
        'currency',
        resolvedItem.currency
          ? currencyOptions.find((o) => o.value === resolvedItem.currency) ??
              null
          : null,
      );
      setValue(
        'amount',
        resolvedItem.amount !== null ? String(resolvedItem.amount) : '',
      );
      setValue('concept', '');
    } else if (isOther) {
      setValue('costCenter', null);
      setValue('currency', null);
      setValue('amount', '');
      setValue('concept', '');
    }
  }, [resolvedItem, isOther, setValue, costCenterOptions, currencyOptions]);

  const chargeItemOptions = useMemo<SelectOptionType[]>(() => {
    const items = chargeItems.map((ci) => ({
      label: ci.name,
      value: ci.id,
    }));
    items.push({ label: t(`${keyPrefix}.other`), value: OTHER_VALUE });
    return items;
  }, [chargeItems, t]);

  const onSubmit = useCallback(
    async (values: FormShape) => {
      const expirationDate = values.expirationDate
        ? toDateOnlyPayload(values.expirationDate)
        : '';
      const studyPlanId = values.studyPlan?.value as number;
      const periodId = values.period?.value as number;

      const body = isOther
        ? {
            studyPlanId,
            periodId,
            concept: values.concept,
            expirationDate,
            costCenter: (values.costCenter?.value as string) ?? '',
            currency: (values.currency?.value as string) ?? undefined,
            amount: values.amount ? Number(values.amount) : undefined,
          }
        : {
            studyPlanId,
            periodId,
            chargeItemId: (values.chargeItem?.value as number) ?? undefined,
            expirationDate,
          };

      const { error } = await createCharge(studentId, body);
      if (!error) {
        addToast({
          icon: 'success',
          color: 'success',
          text: t(`${keyPrefix}.successMessage`),
        });
        onSuccess();
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${keyPrefix}.errorMessage`),
        });
      }
    },
    [isOther, studentId, onSuccess, t],
  );

  const requiredRule = { required: t(`${keyPrefix}.required`) };

  return (
    <Modal isOpen={isOpen} toggle={onClose}>
      <div className="text-center mb-4">
        <h1 className="text-dark fs-22">{t(`${keyPrefix}.title`)}</h1>
      </div>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          {/* Plan de Estudio */}
          <Col xs={12} md={12} className="pb-3">
            <Select
              name="studyPlan"
              control={control}
              label={t(`${keyPrefix}.studyPlan`)}
              placeholder={t(`${keyPrefix}.selectStudyPlan`)}
              options={studyPlanOptions}
              disabled={studyPlanOptions.length === 0}
              rules={requiredRule}
            />
          </Col>

          {/* Período */}
          <Col xs={12} md={12} className="pb-3">
            <Select
              name="period"
              control={control}
              label={t(`${keyPrefix}.period`)}
              placeholder={t(`${keyPrefix}.selectPeriod`)}
              options={periodOptions}
              disabled={selectedStudyPlan === null}
              rules={requiredRule}
            />
          </Col>

          {/* Charge item selector */}
          <Col xs={12} md={12} className="pb-3">
            <Select
              name="chargeItem"
              control={control}
              label={t(`${keyPrefix}.chargeItem`)}
              placeholder={t(`${keyPrefix}.selectChargeItem`)}
              options={chargeItemOptions}
              disabled={!isPlanAndPeriodSelected || chargeItems.length === 0}
              rules={requiredRule}
            />
          </Col>

          {/* Concept — only for "Otro" */}
          {isOther && (
            <Col xs={12} md={12} className="pb-3">
              <TextAreaInput
                name="concept"
                control={control}
                label={t(`${keyPrefix}.concept`)}
                rules={requiredRule}
                showCharCounter
                maxLength={50}
              />
            </Col>
          )}

          {isItemSelected && (
            <>
              {/* Expiration date — always editable */}
              <Col xs={12} md={6} className="pb-3">
                <DateInput
                  name="expirationDate"
                  control={control}
                  label={t(`${keyPrefix}.expirationDate`)}
                  rules={requiredRule}
                />
              </Col>

              {/* Cost center — Select with options derived from chargeItems
                  so "Otro" picks from the same configured set instead of
                  typing free text (HU-15669 fix). */}
              <Col xs={12} md={6} className="pb-3">
                <Select
                  name="costCenter"
                  control={control}
                  label={t(`${keyPrefix}.costCenter`)}
                  options={costCenterOptions}
                  rules={isOther ? requiredRule : undefined}
                  disabled={!isOther}
                />
              </Col>

              {/* Currency */}
              <Col xs={12} md={6} className="pb-3">
                <Select
                  name="currency"
                  control={control}
                  label={t(`${keyPrefix}.currency`)}
                  options={currencyOptions}
                  rules={isOther ? requiredRule : undefined}
                  disabled={!isOther}
                />
              </Col>

              {/* Amount */}
              <Col xs={12} md={6} className="pb-3">
                <TextInput
                  name="amount"
                  control={control}
                  label={t(`${keyPrefix}.amount`)}
                  disabled={!isOther}
                />
              </Col>
            </>
          )}
        </Row>

        {/* Action buttons */}
        <Row className="pt-4">
          <Col xs={12} sm={6}>
            <Button
              text={t(`${keyPrefix}.cancel`)}
              outlined
              fullwidth
              onClick={onClose}
              disabled={isSubmitting}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Button
              type="submit"
              text={t(`${keyPrefix}.save`)}
              fullwidth
              loading={isSubmitting}
              disabled={!isItemSelected || !isPlanAndPeriodSelected}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
